<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { api, messageFrom } from '../api'

interface UserRow {
  id: string
  telegram_user_id: string
  username: string | null
  first_name: string | null
  last_name: string | null
  free_quota_total: number
  free_quota_used: number
  generation_credits: number
  generation_count: string | number
  cost_usd: string | number
  created_at: string
}

const users = ref<UserRow[]>([])
const amounts = ref<Record<string, number>>({})
const busy = ref('')
const error = ref('')
const loading = ref(true)

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    users.value = (await api<{ users: UserRow[] }>('/api/admin/users')).users
    for (const user of users.value) amounts.value[user.id] ??= 1
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function grant(user: UserRow): Promise<void> {
  const amount = Number(amounts.value[user.id] ?? 1)
  busy.value = user.id
  error.value = ''
  try {
    const response = await api<{ generationCredits: number }>(`/api/admin/users/${user.id}/grant`, {
      method: 'POST', body: JSON.stringify({ amount }),
    })
    user.generation_credits = response.generationCredits
  } catch (reason) { error.value = messageFrom(reason) }
  finally { busy.value = '' }
}

onMounted(() => void load())
</script>

<template>
  <main class="page">
    <div class="page-title">
      <div><p class="eyebrow">Аудитория и квоты</p><h2>Юзеры</h2></div>
      <button class="button button--quiet" type="button" @click="load">Обновить</button>
    </div>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="muted">Загружаю пользователей…</p>
    <div v-else class="table-wrap">
      <table>
        <thead><tr><th>Пользователь</th><th>Бесплатные</th><th>Выданные</th><th>Картинки</th><th>Расход</th><th>Выдать</th></tr></thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>
              <strong>{{ [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Без имени' }}</strong>
              <small>@{{ user.username || '—' }} · {{ user.telegram_user_id }}</small>
            </td>
            <td>{{ user.free_quota_used }} / {{ user.free_quota_total }}</td>
            <td>{{ user.generation_credits }}</td>
            <td>{{ user.generation_count }}</td>
            <td>{{ (Number(user.cost_usd) * 80).toFixed(2) }} ₽</td>
            <td>
              <div class="grant-control">
                <input v-model.number="amounts[user.id]" type="number" min="1" max="10000" />
                <button class="button button--quiet" type="button" :disabled="busy === user.id" @click="grant(user)">
                  {{ busy === user.id ? '…' : 'Выдать' }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>
